import React, { useState, Suspense, lazy } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";

// Redirect component for /blog/blog/* to /blog/*
const BlogBlogRedirect = () => {
  const location = useLocation();
  const newPath = location.pathname.replace('/blog/blog/', '/blog/');
  return <Navigate to={newPath} replace />;
};

// AI Crawler Beacon (Phase 17) — fires an image ping on every route change.
// JS-executing crawlers (Perplexity-User, ChatGPT-User in browsing mode,
// OAI-SearchBot, Claude browsing) hit this and get logged server-side.
const AICrawlerBeacon = () => {
  const location = useLocation();
  React.useEffect(() => {
    try {
      const img = new Image();
      img.src = `${process.env.REACT_APP_BACKEND_URL}/api/ai-crawler-beacon?p=${encodeURIComponent(location.pathname)}&t=${Date.now()}`;
    } catch {
      // no-op
    }
  }, [location.pathname]);
  return null;
};

// Blog slug redirects mapping (old slug → new slug)
const blogSlugRedirects = {
  'nykaa-seller-registration-documents-india': 'nykaa-seller-registration-guide-beauty-brands-2025',
  // Session B.6 — cannibalization fixes (Feb 23, 2026)
  'noon-uae-seller-registration-guide-2026': null, // → /noon-seller-registration-uae (see cannibalization redirects)
  'noon-uae-seller-onboarding': null,
  'noon-seller-onboarding-uae': null,
};

// Session B.6 — Cannibalization fix redirect map (client-side fallback for _redirects)
const cannibalizationRedirects = {
  '/services/aza-fashion': '/services/aza-fashions',
  '/services/kalki-fashion': '/kalki-fashion-seller-onboarding',
  '/services/aza': '/services/aza-fashions',
  '/services/pernia': '/services/pernias-pop-up-shop',
  '/services/tira-onboarding': '/tira-seller-onboarding-guide',
  '/services/pernia-seller': '/pernias-pop-up-shop-seller-guide',
  '/blog/noon-uae-seller-registration-guide-2026': '/noon-seller-registration-uae',
  '/blog/noon-uae-seller-onboarding': '/noon-seller-registration-uae',
  '/blog/noon-seller-onboarding-uae': '/noon-seller-registration-uae',
  '/myntra-vs-ajio-seller-platform': '/myntra-vs-ajio',
};

// Component to handle blog slug redirects
const BlogSlugRedirect = () => {
  const location = useLocation();
  const slug = location.pathname.replace('/blog/', '');
  const newSlug = blogSlugRedirects[slug];
  if (newSlug) {
    return <Navigate to={`/blog/${newSlug}`} replace />;
  }
  return null;
};

// Layout - Critical, load immediately
import Navbar from "./components/layout/Navbar";
import Footer from "./components/layout/Footer";
import PrimaryIntentNotice from "./components/seo/PrimaryIntentNotice";
import SiblingIntentLink from "./components/seo/SiblingIntentLink";

// HomePage - Lazy loaded with prefetch hint for better LCP
const HomePage = lazy(() => import(/* webpackPrefetch: true */ "./pages/HomePage"));

// Lazy load non-critical pages for better bundle splitting
const AboutPage = lazy(() => import("./pages/AboutPage"));
const ServicesPage = lazy(() => import("./pages/ServicesPage"));
const MarketplaceDetailPage = lazy(() => import("./pages/MarketplaceDetailPage"));
const ProductsPage = lazy(() => import("./pages/ProductsPage"));
const SuccessStoriesPage = lazy(() => import("./pages/SuccessStoriesPage"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const BlogDetailPage = lazy(() => import("./pages/BlogDetailPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const ComparePricingPage = lazy(() => import("./pages/ComparePricingPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const UnsubscribePage = lazy(() => import("./pages/UnsubscribePage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const LifeAtZarooriPage = lazy(() => import("./pages/LifeAtZarooriPage"));
const TermsOfServicePage = lazy(() => import("./pages/TermsOfServicePage"));
const RefundPolicyPage = lazy(() => import("./pages/RefundPolicyPage"));
const SitemapPage = lazy(() => import("./pages/SitemapPage"));
const TeamPage = lazy(() => import("./pages/TeamPage"));
const QualifiedLeadPage = lazy(() => import("./pages/QualifiedLeadPage"));
const LocationLandingPage = lazy(() => import("./pages/LocationLandingPage"));
const ThankYouPage = lazy(() => import("./pages/ThankYouPage"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));
const MyntraAccountManagementPage = lazy(() => import("./pages/MyntraAccountManagementPage"));
const MyntraPrivateLabelPage = lazy(() => import("./pages/MyntraPrivateLabelPage"));
const MyntraHubPage = lazy(() => import("./pages/MyntraHubPage"));
const TiraServicePage = lazy(() => import("./pages/TiraServicePage"));
const MyntraOnboardingClusterPage = lazy(() => import("./pages/MyntraClusterPages").then(m => ({ default: m.MyntraOnboardingClusterPage })));
const MyntraCataloguingClusterPage = lazy(() => import("./pages/MyntraClusterPages").then(m => ({ default: m.MyntraCataloguingClusterPage })));
const MyntraMarketplaceManagementClusterPage = lazy(() => import("./pages/MyntraClusterPages").then(m => ({ default: m.MyntraMarketplaceManagementClusterPage })));

// Feb 2026 LLM Entity Authority Sprint — new entity/AI-answer/case-study pages
const EntityHubPage = lazy(() => import("./pages/EntityHubPage"));
const EntityPage = lazy(() => import("./pages/EntityPage"));
const OMSClusterPage = lazy(() => import("./pages/OMSClusterPage"));
const BestMyntraOnboardingAgencyPage = lazy(() => import("./pages/BestMyntraOnboardingAgencyPage"));
const MyntraCaseStudiesPage = lazy(() => import("./pages/MyntraCaseStudiesPage"));
const CaseStudyDetailPage = lazy(() => import("./pages/CaseStudyDetailPage"));

// Sprint 2 SEO — 18 programmatic service pages (7 Nykaa + 7 AJIO + 4 Commercial, Feb 2026)
// All wrappers live in /pages/Sprint2ServicePages.jsx and share the `sprint2-services`
// webpack chunk. Routes are rendered via SPRINT2_ROUTE_DEFS.map() inside <Routes>
// (kills the App.js bloat issue and keeps adding more spokes 1-line work).
const sprint2Lazy = (slug) =>
  lazy(() =>
    import(/* webpackChunkName: "sprint2-services" */ "./pages/Sprint2ServicePages").then((m) => {
      const route = m.SPRINT2_ROUTES.find((r) => r.slug === slug);
      return { default: route.Component };
    })
  );

const SPRINT2_ROUTE_DEFS = [
  // Nykaa Fashion (7)
  { slug: 'nykaa-registration-service', path: '/nykaa-registration-service' },
  { slug: 'nykaa-listing-service', path: '/nykaa-listing-service' },
  { slug: 'nykaa-account-management', path: '/nykaa-account-management' },
  { slug: 'nykaa-ads-management', path: '/nykaa-ads-management' },
  { slug: 'nykaa-cataloging-service', path: '/nykaa-cataloging-service' },
  { slug: 'nykaa-reconciliation-service', path: '/nykaa-reconciliation-service' },
  { slug: 'nykaa-growth-consulting', path: '/nykaa-growth-consulting' },
  // AJIO (7)
  { slug: 'ajio-registration-service', path: '/ajio-registration-service' },
  { slug: 'ajio-listing-service', path: '/ajio-listing-service' },
  { slug: 'ajio-account-management', path: '/ajio-account-management' },
  { slug: 'ajio-ads-management', path: '/ajio-ads-management' },
  { slug: 'ajio-cataloging-service', path: '/ajio-cataloging-service' },
  { slug: 'ajio-reconciliation-service', path: '/ajio-reconciliation-service' },
  { slug: 'ajio-growth-consulting', path: '/ajio-growth-consulting' },
  // Commercial / High-Intent (4)
  { slug: 'myntra-account-management-agency', path: '/myntra-account-management-agency' },
  { slug: 'nykaa-account-management-company', path: '/nykaa-account-management-company' },
  { slug: 'ajio-seller-consultant', path: '/ajio-seller-consultant' },
  { slug: 'marketplace-advertising-management', path: '/marketplace-advertising-management' },
].map((r) => ({ ...r, Component: sprint2Lazy(r.slug) }));

// Sprint 3A SEO — 7 Tier-1 commercial pages (4 Myntra + 3 Nykaa, June 2026)
// Highest-intent commercial keywords: consultant / experts / support / health-management
// / beauty-seller-registration / fashion-seller-registration.
// Shares the sprint3-services webpack chunk; routes rendered via SPRINT3_ROUTE_DEFS.map().
const sprint3Lazy = (slug) =>
  lazy(() =>
    import(/* webpackChunkName: "sprint3-services" */ "./pages/Sprint3ServicePages").then((m) => {
      const route = m.SPRINT3_ROUTES.find((r) => r.slug === slug);
      return { default: route.Component };
    })
  );

const SPRINT3_ROUTE_DEFS = [
  { slug: 'myntra-seller-consultant',          path: '/myntra-seller-consultant' },
  { slug: 'myntra-marketplace-experts',        path: '/myntra-marketplace-experts' },
  { slug: 'myntra-seller-support-services',    path: '/myntra-seller-support-services' },
  { slug: 'myntra-account-health-management',  path: '/myntra-account-health-management' },
  { slug: 'nykaa-beauty-seller-registration',  path: '/nykaa-beauty-seller-registration' },
  { slug: 'nykaa-fashion-seller-registration', path: '/nykaa-fashion-seller-registration' },
  { slug: 'nykaa-seller-consultant',           path: '/nykaa-seller-consultant' },
].map((r) => ({ ...r, Component: sprint3Lazy(r.slug) }));

// Sprint 3B SEO — Quick Commerce Cluster (Feb 2026)
// 11 service pages + 1 pillar hub + 1 cross-platform consultant = 12 routes.
// Marketplace-differentiated copy across Blinkit / Swiggy Instamart / Zepto.
const sprint3BLazy = (slug) =>
  lazy(() =>
    import(/* webpackChunkName: "sprint3b-services" */ "./pages/Sprint3BQuickCommercePages").then((m) => {
      const route = m.SPRINT3B_ROUTES.find((r) => r.slug === slug);
      return { default: route.Component };
    })
  );

const SPRINT3B_ROUTE_DEFS = [
  { slug: 'blinkit-seller-onboarding',              path: '/blinkit-seller-onboarding' },
  { slug: 'blinkit-seller-registration',            path: '/blinkit-seller-registration' },
  { slug: 'blinkit-seller-consultant',              path: '/blinkit-seller-consultant' },
  { slug: 'blinkit-account-management',             path: '/blinkit-account-management' },
  { slug: 'swiggy-instamart-seller-onboarding',     path: '/swiggy-instamart-seller-onboarding' },
  { slug: 'swiggy-instamart-seller-registration',   path: '/swiggy-instamart-seller-registration' },
  { slug: 'swiggy-instamart-account-management',    path: '/swiggy-instamart-account-management' },
  { slug: 'zepto-seller-onboarding',                path: '/zepto-seller-onboarding' },
  { slug: 'zepto-seller-registration',              path: '/zepto-seller-registration' },
  { slug: 'zepto-account-management',               path: '/zepto-account-management' },
  { slug: 'quick-commerce-marketplace-services',    path: '/quick-commerce-marketplace-services' },
  { slug: 'quick-commerce-consultant',              path: '/quick-commerce-consultant' },
].map((r) => ({ ...r, Component: sprint3BLazy(r.slug) }));

// Sprint 4 SEO — Marketplace Registration / Brand Approval / Account Mgmt Cluster (Feb 2026)
// 12 commercial pages (10 NEW + 2 FLAGSHIP REBUILDS) targeting high-intent commercial keywords
// across India (Myntra/Nykaa/Tira/AJIO/FirstCry/BigBasket) + UAE (Noon/Namshi/Ounass).
// IMPORTANT: Mounted BEFORE LEAF_PAGE_ROUTES so the two flagship rebuilds
// (/myntra-brand-approval-india, /myntra-seller-account-management) win React Router
// first-match-wins resolution and override the older leaf-route stubs.
const sprint4Lazy = (slug) =>
  lazy(() =>
    import(/* webpackChunkName: "sprint4-services" */ "./pages/Sprint4MarketplaceRegistrationPages").then((m) => {
      const route = m.SPRINT4_ROUTES.find((r) => r.slug === slug);
      return { default: route.Component };
    })
  );

const SPRINT4_ROUTE_DEFS = [
  { slug: 'myntra-seller-registration',         path: '/myntra-seller-registration' },
  { slug: 'myntra-brand-approval-india',        path: '/myntra-brand-approval-india' },
  { slug: 'myntra-seller-account-management',   path: '/myntra-seller-account-management' },
  { slug: 'nykaa-seller-registration',          path: '/nykaa-seller-registration' },
  { slug: 'nykaa-brand-approval',               path: '/nykaa-brand-approval' },
  { slug: 'tira-brand-approval',                path: '/tira-brand-approval' },
  { slug: 'ajio-seller-registration',           path: '/ajio-seller-registration' },
  { slug: 'firstcry-seller-registration',       path: '/firstcry-seller-registration' },
  { slug: 'bigbasket-seller-registration',      path: '/bigbasket-seller-registration' },
  { slug: 'noon-seller-registration-uae',       path: '/noon-seller-registration-uae' },
  { slug: 'namshi-seller-registration-uae',     path: '/namshi-seller-registration-uae' },
  { slug: 'ounass-seller-registration-uae',     path: '/ounass-seller-registration-uae' },
].map((r) => ({ ...r, Component: sprint4Lazy(r.slug) }));

// Sprint 4 GEO Comparison Pages (Feb 2026) — Myntra/Nykaa/Tira head-to-head
const MarketplaceComparisonPage = lazy(() =>
  import(/* webpackChunkName: "sprint4-comparison" */ "./pages/MarketplaceComparisonPage")
);
const SPRINT4_COMPARISON_PATHS = [
  '/myntra-vs-nykaa',
  '/myntra-vs-tira',
  '/nykaa-vs-tira',
];

// P1 Comparison Sprint (Feb 2026) — 8 new head-to-head decision-intent pages
// Fully data-driven: routes auto-derived from marketplaceComparisonData.js
import { COMPARISON_PATHS as V2_COMPARISON_PATHS } from './data/marketplaceComparisonData';
const MarketplaceComparisonPageV2 = lazy(() =>
  import(/* webpackChunkName: "comparison-v2" */ "./pages/MarketplaceComparisonPageV2")
);

// P1.5 Alternatives Sprint (Feb 2026) — 8 "Best Alternatives to X" pages
import { ALT_PATHS } from './data/marketplaceAlternativesData';
const MarketplaceAlternativesPage = lazy(() =>
  import(/* webpackChunkName: "alternatives" */ "./pages/MarketplaceAlternativesPage")
);

// P2 Industry Sprint (Feb 2026) — 52 "{Marketplace} {Industry} Consultant" pages
import { PAGE_PATHS as INDUSTRY_PATHS } from './data/marketplaceIndustryData';
const MarketplaceIndustryPage = lazy(() =>
  import(/* webpackChunkName: "industry" */ "./pages/MarketplaceIndustryPage")
);

// Sprint 3 Deep-Topic Pages (Feb 2026) — 70 "{Marketplace} {Topic}" pages
import { TOPIC_PATHS } from './data/marketplaceTopicData';
const MarketplaceTopicPage = lazy(() =>
  import(/* webpackChunkName: "mkt-topic" */ "./pages/MarketplaceTopicPage")
);

// Session B — Marketplace Authority Cluster (Feb 23, 2026) — 15 mkt × 22 topics = 330 pages
// URL pattern: /marketplace/:marketplaceSlug/:topicSlug
const MarketplaceAuthorityPage = lazy(() =>
  import(/* webpackChunkName: "mkt-authority" */ "./pages/MarketplaceAuthorityPage")
);

// Sprint 1 OMS Foundation (Feb 2026) — 18 OMS authority pages
import { OMS_PATHS, PILLAR_PAGES as OMS_PILLARS, COMPARISON_PAGES as OMS_COMPARISONS, ALTERNATIVES_PAGES as OMS_ALTERNATIVES } from './data/omsData';
const OMSHubPage = lazy(() => import(/* webpackChunkName: "oms-hub" */ "./pages/OMSHubPage"));
const OMSComparisonPage = lazy(() => import(/* webpackChunkName: "oms-cmp" */ "./pages/OMSComparisonPage"));
const OMSAlternativesPage = lazy(() => import(/* webpackChunkName: "oms-alt" */ "./pages/OMSAlternativesPage"));
const OMSPillarPage = lazy(() => import(/* webpackChunkName: "oms-pillar" */ "./pages/OMSPillarPage"));
const OMSPricingPage = lazy(() => import(/* webpackChunkName: "oms-pricing" */ "./pages/OMSPricingPage"));
const OMSFeaturesPage = lazy(() => import(/* webpackChunkName: "oms-features" */ "./pages/OMSFeaturesPage"));
const OMSROICalculator = lazy(() => import(/* webpackChunkName: "oms-roi" */ "./pages/OMSROICalculator"));
const KnowledgeGraphExplorer = lazy(() => import(/* webpackChunkName: "kg-explorer" */ "./pages/KnowledgeGraphExplorer"));

// Sprint 2 — OMS-for-Marketplace + Industry OMS (28 pages, Feb 2026)
import { SPRINT2_PATHS } from './data/omsSprint2Data';
const OMSSprint2Page = lazy(() => import(/* webpackChunkName: "oms-sprint2" */ "./pages/OMSSprint2Page"));

// Phase 3 AI-Answer Pages (Feb 2026) — 116 AI-optimized Q&A pages under /answers/*
const AIAnswersIndex = lazy(() => import(/* webpackChunkName: "answers-index" */ "./pages/AIAnswersIndex"));
const AIAnswerPage = lazy(() => import(/* webpackChunkName: "answer-page" */ "./pages/AIAnswerPage"));

// Sprint 4 Phase 2 — Authority Blog Cluster (Feb 2026)
// Sprint 3C update: slugs auto-derived from BLOG_POSTS so any new authority post
// is routed without touching App.js.
import { BLOG_POSTS as AUTHORITY_BLOG_POSTS } from './data/authorityBlogCluster';
const AuthorityBlogPost = lazy(() =>
  import(/* webpackChunkName: "authority-blog" */ "./pages/AuthorityBlogPost")
);
const BlogHubPage = lazy(() =>
  import(/* webpackChunkName: "blog-hub" */ "./pages/BlogHubPage")
);
const AUTHORITY_BLOG_SLUGS = AUTHORITY_BLOG_POSTS.map((p) => p.slug);

// Phase 1 SEO Sprint (Feb 2026) — Topic Cluster hub pages (17 total).
// 9 marketplace-specific + 8 category horizontal hubs. Routes auto-derived.
import { ALL_TOPIC_CLUSTERS } from './data/topicClusterData';
const TopicClusterPage = lazy(() =>
  import(/* webpackChunkName: "topic-cluster" */ './pages/TopicClusterPage')
);

// Sprint 1 SEO — Marketplace Account Management Hub + 7 Myntra service spokes (Feb 2026)
// All 8 pages live in a single chunk via shared `webpackChunkName` (smaller bundle, faster route nav)
// → MIGRATED to /app/frontend/src/routes/leafRoutes.js (Feb 2026 — App.js route-bloat refactor)
//
// Pages that needed dedicated treatment (wrappers, dynamic params, redirects)
// remain inline below. Programmatic-SEO leaf routes are now centralized.

// ─── LEAF page imports → centralized in /routes/leafRoutes.js ─────────
// All single-Component, exact-path routes (no schema wrappers, no params,
// no redirects) live in LEAF_PAGE_ROUTES. Rendered via a single .map() below.
import { LEAF_PAGE_ROUTES } from "./routes/leafRoutes";

// Marketplace Knowledge Graph — dynamic :slug routes, kept inline
const MarketplaceHubPage = lazy(() => import(/* webpackChunkName: "marketplace-kg" */ "./pages/MarketplaceHubPage"));
const MarketplaceEntityPage = lazy(() => import(/* webpackChunkName: "marketplace-kg" */ "./pages/MarketplaceEntityPage"));
const MarketplaceComparePage = lazy(() => import(/* webpackChunkName: "marketplace-kg" */ "./pages/MarketplaceComparePage"));

// LLM/GEO Dynamic :slug Pages (kept inline — dynamic params)
import { MyntraGEOPage, NykaaGEOPage, AjioGEOPage } from "./pages/GEOPage";

// Lazy load non-critical components
const AIChatbot = lazy(() => import("./components/chat/AIChatbot"));
const WhatsAppButton = lazy(() => import("./components/chat/WhatsAppButton"));
const StickyQuoteButton = lazy(() => import("./components/conversion/StickyQuoteButton"));
const DemoButton = lazy(() => import("./components/demo/DemoButton"));
const LeadQualifier = lazy(() => import("./components/sections/LeadQualifier"));
const SmartConversionLayer = lazy(() => import("./components/SmartConversionLayer"));

// Critical SEO components — GA4 + TrackingPixels + RedirectHandler are tiny and
// run on every page so we keep them eager. AdvancedSchemas / AEOContent / LEOContent
// are heavier (combined ~4.5k lines of static schema literals) and only mount on
// a subset of routes via <Route element={...}>. Phase E (Feb 2026) — switched to
// React.lazy() so their bundle splits out and is fetched only when those routes
// are visited (saves ~25-40 KB on the initial JS bundle for non-SEO routes).
import RedirectHandler from "./components/RedirectHandler";
import TitleOverrideProvider from "./components/seo/TitleOverrideProvider";
import GA4PageTracker from "./components/seo/GA4PageTracker";
import TrackingPixels from "./components/seo/TrackingPixels";
import { useAttributionTracker } from "./hooks/useAttributionTracker";

const AttributionTracker = () => {
  useAttributionTracker();
  return null;
};

const AdvancedSchemas = lazy(() => import(/* webpackChunkName: "seo-helmet" */ "./components/seo/AdvancedSchemas"));
const AEOContent = lazy(() => import(/* webpackChunkName: "seo-helmet" */ "./components/seo/AEOContent"));
const LEOContent = lazy(() => import(/* webpackChunkName: "seo-helmet" */ "./components/seo/LEOContent"));

// Loading fallback component for Suspense
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <div className="w-12 h-12 border-4 border-pink-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-gray-500">Loading...</p>
    </div>
  </div>
);

// Social Sidebar
const SocialSidebar = () => {
  const socials = [
    { name: 'Facebook', color: 'bg-blue-600', icon: 'f', url: 'https://www.facebook.com/zarooriretailpremium' },
    { name: 'X', color: 'bg-black', icon: '𝕏', url: 'https://x.com/zarooriretail' },
    { name: 'LinkedIn', color: 'bg-blue-700', icon: 'in', url: 'https://www.linkedin.com/company/100707300/' },
    { name: 'YouTube', color: 'bg-red-600', icon: '▶', url: 'https://www.youtube.com/@ZarooriRetailYT' },
    { name: 'Instagram', color: 'bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400', icon: '📷', url: 'https://www.instagram.com/zarooriretail' },
    { name: 'Pinterest', color: 'bg-red-700', icon: 'P', url: 'https://in.pinterest.com/Zarooriretail/' },
  ];

  return (
    <div className="fixed left-0 top-1/2 -translate-y-1/2 z-40 hidden lg:flex flex-col gap-1">
      {socials.map((social, i) => (
        <a
          key={i}
          href={social.url}
          target="_blank"
          rel="noopener noreferrer"
          className={`w-8 h-8 ${social.color} text-white flex items-center justify-center text-xs font-bold hover:w-10 transition-all`}
          title={social.name}
        >
          {social.icon}
        </a>
      ))}
    </div>
  );
};

const FloatingWidgets = () => {
  const { pathname } = useLocation();
  if (pathname.startsWith('/admin')) return null;
  return (
    <>
      <DemoButton />
      <WhatsAppButton />
      <StickyQuoteButton />
      <AIChatbot />
      <SmartConversionLayer />
    </>
  );
};

const AdminAwareChrome = () => {
  const { pathname } = useLocation();
  if (pathname.startsWith('/admin')) return null;
  return (
    <>
      <Navbar />
      <SocialSidebar />
    </>
  );
};

const AdminAwareFooter = () => {
  const { pathname } = useLocation();
  if (pathname.startsWith('/admin')) return null;
  return <Footer />;
};


function App() {
  const [showLeadQualifier, setShowLeadQualifier] = useState(false);
  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <GA4PageTracker />
          <TrackingPixels />
          <RedirectHandler />
          <AttributionTracker />
          <AICrawlerBeacon />
          <TitleOverrideProvider />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Standalone Landing Pages - No Navbar/Footer for Meta Ads */}
              <Route path="/brand-eligibility" element={<QualifiedLeadPage />} />
              <Route path="/brand-eligibility/thank-you" element={<ThankYouPage />} />
              <Route path="/brand-eligibility/:city" element={<LocationLandingPage />} />
              <Route path="/meta-landing" element={<QualifiedLeadPage />} />
              <Route path="/meta-landing/thank-you" element={<ThankYouPage />} />
              <Route path="/meta-landing/:city" element={<LocationLandingPage />} />
              
              {/* Regular Routes with Navbar/Footer */}
              <Route path="/*" element={
                <>
                  <AdminAwareChrome />
                  <main className="min-h-screen">
                    <Routes>
                      {/* Session B.6 — Cannibalization redirects (Feb 23, 2026) — MUST be first */}
                      {Object.entries(cannibalizationRedirects).map(([from, to]) => (
                        <Route key={`redir-${from}`} path={from} element={<Navigate to={to} replace />} />
                      ))}
                      <Route path="/" element={<><AdvancedSchemas page="home" /><AEOContent page="home" /><LEOContent location="all" /><HomePage /></>} />
                      <Route path="/about" element={<><AdvancedSchemas page="about" /><LEOContent location="all" /><AboutPage /></>} />
                      <Route path="/services" element={<><AdvancedSchemas page="services" /><ServicesPage /></>} />
                      <Route path="/services/myntra-private-label" element={<MyntraPrivateLabelPage />} />
                      <Route path="/services/myntra-onboarding" element={<MyntraOnboardingClusterPage />} />
                      <Route path="/services/myntra-cataloguing" element={<MyntraCataloguingClusterPage />} />
                      <Route path="/services/myntra-marketplace-management" element={<MyntraMarketplaceManagementClusterPage />} />
                      <Route path="/services/myntra-account-management" element={<MyntraAccountManagementPage />} />
                      <Route path="/services/tira" element={<TiraServicePage />} />
                      <Route path="/myntra" element={<MyntraHubPage />} />
                      <Route path="/myntra-seller-onboarding" element={<MyntraOnboardingClusterPage />} />
                      <Route path="/myntra-account-management" element={<MyntraAccountManagementPage />} />
                      <Route path="/services/:slug" element={<MarketplaceDetailPage />} />
                      <Route path="/marketplaces" element={<MarketplaceHubPage />} />
                      <Route path="/marketplaces/:slug" element={<MarketplaceEntityPage />} />
                      <Route path="/marketplace-compare" element={<MarketplaceComparePage />} />
                      <Route path="/products" element={<><AdvancedSchemas page="products" /><ProductsPage /></>} />
                      <Route path="/pricing" element={<><AdvancedSchemas page="products" /><ProductsPage /></>} />
                      <Route path="/success-stories" element={<SuccessStoriesPage />} />
                      {/* Sprint 4 Phase 2 — Authority Blog Cluster (Feb 2026) */}
                      {/* MOUNTED BEFORE legacy /blog routes so new hub + authority posts win first-match-wins */}
                      <Route path="/blog" element={<BlogHubPage />} />
                      {AUTHORITY_BLOG_SLUGS.map((slug) => (
                        <Route
                          key={`authority-blog-${slug}`}
                          path={`/blog/${slug}`}
                          element={<AuthorityBlogPost />}
                        />
                      ))}
                      <Route path="/blog" element={<><AdvancedSchemas page="blog" /><BlogPage /></>} />
                      {/* Phase 1 SEO Sprint — Topic Cluster hubs (17 pages) */}
                      {ALL_TOPIC_CLUSTERS.map((c) => (
                        <Route
                          key={c.slug}
                          path={c.canonicalPath}
                          element={<TopicClusterPage slug={c.slug} />}
                        />
                      ))}
                      {/* Blog slug redirects - must be before generic :slug route */}
                      <Route path="/blog/nykaa-seller-registration-documents-india" element={<Navigate to="/blog/nykaa-seller-registration-guide-beauty-brands-2025" replace />} />
                      <Route path="/blog/:slug" element={<BlogDetailPage />} />
                      {/* Redirect /blog/blog/slug to /blog/slug - fix duplicate path issue */}
                      <Route path="/blog/blog/*" element={<BlogBlogRedirect />} />
                      <Route path="/contact" element={<><AdvancedSchemas page="contact" /><LEOContent location="all" /><ContactPage /></>} />
                      <Route path="/compare-pricing" element={<ComparePricingPage />} />
                      <Route path="/pricing-calculator" element={<ComparePricingPage />} />                      <Route path="/admin" element={<AdminPage />} />
                      <Route path="/unsubscribe/:token" element={<UnsubscribePage />} />
                      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                      <Route path="/life-at-zaroori" element={<LifeAtZarooriPage />} />
                      <Route path="/terms-of-service" element={<TermsOfServicePage />} />
                      <Route path="/refund-policy" element={<RefundPolicyPage />} />
                      <Route path="/sitemap" element={<SitemapPage />} />
                      <Route path="/team" element={<TeamPage />} />
                      {/* Pillar Pages - SEO Money Pages — see /routes/leafRoutes.js */}
                      {/* Sprint 2 SEO — 18 programmatic service pages (Nykaa + AJIO + Commercial, Feb 2026) */}
                      {SPRINT2_ROUTE_DEFS.map(({ path, slug, Component }) => (
                        <Route key={slug} path={path} element={<Component />} />
                      ))}
                      {/* Sprint 3A SEO — 7 Tier-1 commercial pages (June 2026) */}
                      {SPRINT3_ROUTE_DEFS.map(({ path, slug, Component }) => (
                        <Route key={slug} path={path} element={<Component />} />
                      ))}
                      {/* Sprint 3B SEO — Quick Commerce Cluster: Blinkit + Swiggy Instamart + Zepto (Feb 2026) */}
                      {SPRINT3B_ROUTE_DEFS.map(({ path, slug, Component }) => (
                        <Route key={slug} path={path} element={<Component />} />
                      ))}
                      {/* Sprint 4 SEO — Marketplace Registration / Brand Approval / Account Mgmt (Feb 2026) */}
                      {/* Mounted BEFORE LEAF_PAGE_ROUTES so flagship rebuilds win first-match-wins */}
                      {SPRINT4_ROUTE_DEFS.map(({ path, slug, Component }) => (
                        <Route key={slug} path={path} element={<Component />} />
                      ))}
                      {/* Sprint 4 GEO Comparison Pages (Feb 2026) — slug param drives content */}
                      {SPRINT4_COMPARISON_PATHS.map((p) => (
                        <Route
                          key={p}
                          path={p}
                          element={<MarketplaceComparisonPage />}
                        />
                      ))}
                      {/* P1 Comparison Sprint (Feb 2026) — 8 data-driven head-to-head pages */}
                      {V2_COMPARISON_PATHS.map((p) => (
                        <Route
                          key={p}
                          path={p}
                          element={<MarketplaceComparisonPageV2 />}
                        />
                      ))}
                      {/* P1.5 Alternatives Sprint (Feb 2026) — 8 "Best Alternatives to X" pages */}
                      {ALT_PATHS.map((p) => (
                        <Route
                          key={p}
                          path={p}
                          element={<MarketplaceAlternativesPage />}
                        />
                      ))}
                      {/* P2 Industry Sprint (Feb 2026) — 52 "{Marketplace} {Industry} Consultant" pages */}
                      {INDUSTRY_PATHS.map((p) => (
                        <Route
                          key={p}
                          path={p}
                          element={<MarketplaceIndustryPage />}
                        />
                      ))}
                      {/* Sprint 3 Deep-Topic Pages (Feb 2026) — 70 "{Marketplace} {Topic}" pages */}
                      {TOPIC_PATHS.map((p) => (
                        <Route
                          key={p}
                          path={p}
                          element={<MarketplaceTopicPage />}
                        />
                      ))}
                      {/* Session B — Marketplace Authority Cluster (Feb 23, 2026) — 330 pages under /marketplace/:mkt/:topic */}
                      <Route path="/marketplace/:marketplaceSlug/:topicSlug" element={<MarketplaceAuthorityPage />} />
                      {/* Sprint 1 OMS Foundation (Feb 2026) — 18 OMS authority pages
                          Phase X.6 (Feb 24 2026) — CANONICAL MIGRATION /oms/* → /zaroori-jini-oms/*
                          to build Zaroori Jini OMS as an independent SoftwareApplication entity. */}
                      <Route path="/oms" element={<Navigate to="/zaroori-jini-oms" replace />} />
                      <Route path="/oms/pricing" element={<Navigate to="/zaroori-jini-oms/pricing" replace />} />
                      <Route path="/oms/features" element={<Navigate to="/zaroori-jini-oms/features" replace />} />
                      <Route path="/oms/roi-calculator" element={<Navigate to="/zaroori-jini-oms/roi-calculator" replace />} />
                      <Route path="/zaroori-jini-oms" element={<OMSHubPage />} />
                      <Route path="/zaroori-jini-oms/pricing" element={<OMSPricingPage />} />
                      <Route path="/zaroori-jini-oms/features" element={<OMSFeaturesPage />} />
                      <Route path="/zaroori-jini-oms/roi-calculator" element={<OMSROICalculator />} />
                      <Route path="/knowledge-graph" element={<KnowledgeGraphExplorer />} />
                      {/* Sprint 2 — 28 OMS-for-Marketplace + Industry OMS pages */}
                      {SPRINT2_PATHS.map((p) => (
                        <Route key={`oms-sprint2-${p}`} path={p} element={<OMSSprint2Page />} />
                      ))}
                      {OMS_PILLARS.map((p) => (
                        <Route key={`oms-pillar-${p.slug}`} path={`/zaroori-jini-oms/${p.slug}`} element={<OMSPillarPage />} />
                      ))}
                      {OMS_COMPARISONS.map((p) => (
                        <Route key={`oms-cmp-${p.slug}`} path={`/${p.slug}`} element={<OMSComparisonPage />} />
                      ))}
                      {OMS_ALTERNATIVES.map((p) => (
                        <Route key={`oms-alt-${p.slug}`} path={`/${p.slug}`} element={<OMSAlternativesPage />} />
                      ))}
                      {/* Phase 3 AI-Answer Pages (Feb 2026) — /answers/* dynamic */}
                      <Route path="/answers" element={<AIAnswersIndex />} />
                      <Route path="/answers/:slug" element={<AIAnswerPage />} />

                      {/* Feb 2026 — LLM Entity Authority Sprint (Phases 1, 2, 3) */}
                      <Route path="/entity/zaroori-retail" element={<EntityHubPage />} />
                      <Route path="/entity/:slug" element={<EntityPage />} />
                      <Route path="/best-myntra-onboarding-agency-india" element={<BestMyntraOnboardingAgencyPage />} />
                      <Route path="/case-studies/myntra" element={<MyntraCaseStudiesPage />} />
                      <Route path="/case-studies/:slug" element={<CaseStudyDetailPage />} />

                      {/* Feb 2026 — Session A OMS Cluster (9 new routes; /oms already routed above) */}
                      <Route path="/ai-oms" element={<OMSClusterPage forcedSlug="ai-oms" />} />
                      <Route path="/multi-marketplace-oms" element={<OMSClusterPage forcedSlug="multi-marketplace-oms" />} />
                      <Route path="/order-management-system" element={<OMSClusterPage forcedSlug="order-management-system" />} />
                      <Route path="/inventory-management" element={<OMSClusterPage forcedSlug="inventory-management" />} />
                      <Route path="/warehouse-management" element={<OMSClusterPage forcedSlug="warehouse-management" />} />
                      <Route path="/marketplace-integration" element={<OMSClusterPage forcedSlug="marketplace-integration" />} />
                      <Route path="/shopify-integration" element={<OMSClusterPage forcedSlug="shopify-integration" />} />
                      <Route path="/catalog-management" element={<OMSClusterPage forcedSlug="catalog-management" />} />
                      {/* Sprint 4 Phase 2 — Authority Blog Cluster is now mounted higher in this Routes block,
                          above the legacy /blog routes, so first-match-wins picks the new pages. */}

                      {/* ============================================ */}
                      {/* LEAF PAGES (~100 routes) — centralized in    */}
                      {/* /app/frontend/src/routes/leafRoutes.js        */}
                      {/* All single-Component, exact-path routes:      */}
                      {/*   Sprint 1 spokes, pillars, agencies, kids,   */}
                      {/*   city × marketplace, Amazon, pet, global,    */}
                      {/*   comparisons, blog series.                   */}
                      {/* ============================================ */}
                      {LEAF_PAGE_ROUTES.map(({ path, Component }) => (
                        <Route key={path} path={path} element={<Component />} />
                      ))}

                      {/* Redirects (kept inline — special elements) */}
                      <Route path="/blog/premium-marketplace-access-india-report-2026" element={<Navigate to="/premium-marketplace-access-india-report-2026" replace />} />
                      <Route path="/firstcry-brand-onboarding" element={<Navigate to="/firstcry" replace />} />

                      {/* LLM/GEO CLUSTER PAGES — dynamic :slug routes, kept inline */}
                      <Route path="/myntra/:slug" element={<MyntraGEOPage />} />
                      <Route path="/nykaa/:slug" element={<NykaaGEOPage />} />
                      <Route path="/ajio/:slug" element={<AjioGEOPage />} />

                      {/* 404 Page - Must be last */}
                      <Route path="*" element={<NotFoundPage />} />
                    </Routes>
                  </main>
                  <PrimaryIntentNotice />
                  <SiblingIntentLink />
                  <AdminAwareFooter />
                  <Suspense fallback={null}>
                    <FloatingWidgets />
                  </Suspense>
                  {showLeadQualifier && <LeadQualifier onClose={() => setShowLeadQualifier(false)} />}
                </>
              } />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
